/**
 * CustomerInvoicesModal — full financial history for a single customer with
 * every action the main Invoices tab exposes:
 *
 *   • Preview  — opens the InvoicePreviewModal (with in-modal PDF download)
 *   • PDF      — direct download via generateInvoicePDF
 *   • Email    — pushes user to /invoices with prefill so the DocumentEmailModal opens
 *   • Edit     — pushes user to /invoices, opens the edit form
 *   • Pay      — pushes user to /invoices, opens the payment modal (hidden when paid)
 *   • Duplicate/Delete — pushes user to /invoices, runs the action
 *
 * Handing off "stateful" actions via a sessionStorage prefill + navigation
 * keeps a single source of truth on the Invoices page (form state, payment
 * state, email template, etc.) instead of duplicating hundreds of lines.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, FileText, DollarSign, Receipt, Eye, Printer, Mail, Edit, Copy, Trash2, Bell,
} from 'lucide-react';
import { toast } from 'sonner';
import { parseInvoiceDate } from '../../utils/invoiceHelpers';
import { generateInvoicePDF } from '../../utils/PDFGenerator';
import InvoicePreviewModal from '../invoices/InvoicePreviewModal';

const STATUS_STYLE = {
  paid:    { bg: '#dcfce7', color: '#166534', label: 'Paid' },
  pending: { bg: '#fef3c7', color: '#92400e', label: 'Pending' },
  partial: { bg: '#dbeafe', color: '#1e40af', label: 'Partial' },
  draft:   { bg: '#f1f5f9', color: '#475569', label: 'Draft' },
  overdue: { bg: '#fee2e2', color: '#991b1b', label: 'Overdue' },
};

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const dateLabel = (v) => {
  const d = parseInvoiceDate(v);
  return d ? d.toLocaleDateString() : '—';
};

const StatusPill = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.draft;
  return (
    <span
      style={{
        display: 'inline-block', padding: '3px 10px', borderRadius: 999,
        fontSize: 11, fontWeight: 700, letterSpacing: 0.2,
        background: s.bg, color: s.color,
      }}
    >
      {s.label}
    </span>
  );
};

const KpiTile = ({ label, value, tint }) => (
  <div style={{
    flex: 1, minWidth: 130, padding: '14px 16px',
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
    borderLeft: `4px solid ${tint}`,
  }}>
    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.4, color: '#64748b' }}>
      {label.toUpperCase()}
    </div>
    <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginTop: 4 }}>
      {value}
    </div>
  </div>
);

// Compact action button used inside a row. Prevents row-level clicks from
// firing so the icons act independently of the row-body "Preview" affordance.
const RowIconBtn = ({ title, testid, icon: Icon, tint, onClick, disabled }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    disabled={disabled}
    data-testid={testid}
    onClick={(e) => { e.stopPropagation(); if (!disabled) onClick(); }}
    style={{
      all: 'unset',
      cursor: disabled ? 'not-allowed' : 'pointer',
      width: 32, height: 32, borderRadius: 8,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: disabled ? '#f1f5f9' : `${tint}15`,
      color: disabled ? '#94a3b8' : tint,
      transition: 'background 0.12s, transform 0.12s',
      flexShrink: 0,
    }}
    onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = `${tint}28`; }}
    onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.background = `${tint}15`; }}
  >
    <Icon size={15} />
  </button>
);

// Reads company info + signature saved on the Settings page. Shared with
// PDF/preview so the customer sees the same branding whatever entry point
// they hit.
const getSettings = () => {
  try {
    const raw = localStorage.getItem('maidly_settings');
    const parsed = raw ? JSON.parse(raw) : {};
    return {
      companyInfo: parsed.companyInfo || {},
      companySignature: parsed.companySignature || '',
    };
  } catch {
    return { companyInfo: {}, companySignature: '' };
  }
};

// Hydrates the row with the latest customer contact info so PDFs render the
// right "Bill To". Mirrors what Invoices.js does at render-time.
const enrichForPdf = (invoice, customer) => ({
  ...invoice,
  customerName: invoice.customerName || customer?.name || '',
  customerEmail: invoice.customerEmail || customer?.email || '',
  customerPhone: invoice.customerPhone || customer?.phone || '',
  customerAddress: invoice.customerAddress || customer?.address || '',
  customerContactName: invoice.customerContactName || customer?.contactName || '',
});

export default function CustomerInvoicesModal({ customer, onClose }) {
  const [tab, setTab] = useState('all');
  const [invoices, setInvoices] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const printRef = useRef(null);
  const navigate = useNavigate();
  const { companyInfo, companySignature } = useMemo(getSettings, []);

  useEffect(() => {
    if (!customer) return;
    const allInvoices = JSON.parse(localStorage.getItem('maidly_invoices') || '[]');
    const allEstimates = JSON.parse(localStorage.getItem('maidly_quotes') || '[]');
    const belongsTo = (row) =>
      (customer.id && row.customerId === customer.id) ||
      (!row.customerId && row.customerName && customer.name &&
        row.customerName.toLowerCase() === customer.name.toLowerCase());
    setInvoices(allInvoices.filter(belongsTo));
    setEstimates(allEstimates.filter(belongsTo));
  }, [customer]);

  const stats = useMemo(() => {
    const totalBilled = invoices.reduce((s, i) => s + Number(i.total || 0), 0);
    const totalPaid = invoices.reduce((s, i) => s + Number(i.paidAmount || (i.status === 'paid' ? i.total : 0)), 0);
    const outstanding = Math.max(0, totalBilled - totalPaid);
    return { totalBilled, totalPaid, outstanding, count: invoices.length };
  }, [invoices]);

  const rows = useMemo(() => {
    const sorted = [...invoices].sort((a, b) => {
      const da = new Date(a.createdAt || a.date || 0).getTime();
      const db = new Date(b.createdAt || b.date || 0).getTime();
      return db - da;
    });
    if (tab === 'all') return sorted;
    if (tab === 'estimates') {
      return [...estimates].sort((a, b) => {
        const da = new Date(a.createdAt || a.date || 0).getTime();
        const db = new Date(b.createdAt || b.date || 0).getTime();
        return db - da;
      });
    }
    return sorted.filter((inv) => inv.status === tab);
  }, [tab, invoices, estimates]);

  // ---- Row actions ---------------------------------------------------------

  const openPreview = (row) => {
    const isEstimate = tab === 'estimates';
    setPreviewInvoice({
      ...enrichForPdf(row, customer),
      _docType: isEstimate ? 'estimate' : 'invoice',
    });
  };

  const downloadPdf = (row) => {
    generateInvoicePDF(enrichForPdf(row, customer), companyInfo);
    toast.success(`${tab === 'estimates' ? 'Estimate' : 'Invoice'} PDF downloaded`);
  };

  // Hands the row off to /invoices with a prefill that tells the page which
  // action to run when it boots. Invoices.js listens for this same event
  // when it's already mounted.
  const handOffToInvoices = (row, action) => {
    const payload = { invoiceId: row.id, action, ts: Date.now() };
    try {
      sessionStorage.setItem('maidly_invoice_action', JSON.stringify(payload));
      window.dispatchEvent(new CustomEvent('maidly:invoice-action', { detail: payload }));
    } catch { /* ignore */ }
    onClose && onClose();
    navigate('/invoices');
  };

  if (!customer) return null;

  return (
    <>
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20, zIndex: 1200,
      }}
      data-testid="customer-invoices-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 980, maxHeight: '92vh',
          background: '#f8fafc', borderRadius: 16, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 60px rgba(15,23,42,0.35)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '18px 22px', background: '#fff',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 42, height: 42, borderRadius: 12,
            background: '#ec489915', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <Receipt size={22} color="#ec4899" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
              {customer.name}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              Invoice & payment history · click any row to preview
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="customer-invoices-close"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: 6, borderRadius: 6, color: '#64748b',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* KPIs */}
        <div style={{
          padding: '14px 22px', display: 'flex', gap: 10,
          flexWrap: 'wrap', background: '#f8fafc',
          borderBottom: '1px solid #e5e7eb',
        }}>
          <KpiTile label="Invoices" value={stats.count} tint="#3b82f6" />
          <KpiTile label="Total Billed" value={money(stats.totalBilled)} tint="#8b5cf6" />
          <KpiTile label="Total Paid" value={money(stats.totalPaid)} tint="#22c55e" />
          <KpiTile
            label="Outstanding"
            value={money(stats.outstanding)}
            tint={stats.outstanding > 0 ? '#ef4444' : '#94a3b8'}
          />
        </div>

        {/* Filter tabs */}
        <div style={{
          padding: '12px 22px', background: '#fff',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex', gap: 6, flexWrap: 'wrap',
        }}>
          {[
            { key: 'all', label: `All (${invoices.length})` },
            { key: 'pending', label: 'Pending' },
            { key: 'partial', label: 'Partial' },
            { key: 'paid', label: 'Paid' },
            { key: 'draft', label: 'Drafts' },
            { key: 'estimates', label: `Estimates (${estimates.length})` },
          ].map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`customer-invoices-tab-${t.key}`}
                style={{
                  all: 'unset', cursor: 'pointer',
                  padding: '6px 14px', borderRadius: 999,
                  fontSize: 12, fontWeight: 700,
                  background: active ? '#0073ea' : 'transparent',
                  color: active ? '#fff' : '#475569',
                  border: `1px solid ${active ? '#0073ea' : '#e5e7eb'}`,
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Rows */}
        <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
          {rows.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '48px 20px', color: '#94a3b8',
            }}>
              <FileText size={40} strokeWidth={1.5} style={{ marginBottom: 8, opacity: 0.5 }} />
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                No {tab === 'all' ? 'invoices' : tab} yet
              </div>
              <div style={{ fontSize: 12, marginTop: 4 }}>
                {tab === 'estimates'
                  ? 'Estimates you create for this customer will appear here.'
                  : 'Invoices for this customer will appear here.'}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {rows.map((row) => {
                const isPaid = row.status === 'paid';
                const isEstimate = tab === 'estimates';
                return (
                  <div
                    key={row.id}
                    onClick={() => openPreview(row)}
                    data-testid={`customer-invoice-row-${row.id}`}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') openPreview(row); }}
                    style={{
                      cursor: 'pointer',
                      background: '#fff', border: '1px solid #e5e7eb',
                      borderRadius: 12, padding: '12px 14px',
                      display: 'flex', alignItems: 'center', gap: 12,
                      transition: 'border-color 0.12s, box-shadow 0.12s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#cbd5e1';
                      e.currentTarget.style.boxShadow = '0 3px 12px rgba(15,23,42,0.08)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{
                      width: 34, height: 34, borderRadius: 10,
                      background: '#0073ea10', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      {isEstimate ? (
                        <FileText size={16} color="#0073ea" />
                      ) : (
                        <DollarSign size={16} color="#0073ea" />
                      )}
                    </div>

                    {/* Meta */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        flexWrap: 'wrap', marginBottom: 2,
                      }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                          {row.invoiceNumber || row.quoteNumber || `#${String(row.id || '').slice(0, 6)}`}
                        </span>
                        <StatusPill status={row.status} />
                      </div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>
                        {dateLabel(row.date || row.createdAt)}
                        {row.dueDate && ` · Due ${dateLabel(row.dueDate)}`}
                      </div>
                    </div>

                    {/* Amount */}
                    <div style={{ textAlign: 'right', flexShrink: 0, marginRight: 6 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                        {money(row.total)}
                      </div>
                      {Number(row.paidAmount) > 0 && !isPaid && (
                        <div style={{ fontSize: 11, color: '#16a34a', marginTop: 2 }}>
                          Paid {money(row.paidAmount)}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div
                      style={{
                        display: 'flex', gap: 4, flexShrink: 0,
                        borderLeft: '1px solid #f1f5f9', paddingLeft: 10,
                      }}
                    >
                      <RowIconBtn
                        title="Preview"
                        testid={`customer-inv-preview-${row.id}`}
                        icon={Eye}
                        tint="#0073ea"
                        onClick={() => openPreview(row)}
                      />
                      <RowIconBtn
                        title="Download PDF"
                        testid={`customer-inv-pdf-${row.id}`}
                        icon={Printer}
                        tint="#64748b"
                        onClick={() => downloadPdf(row)}
                      />
                      {!isEstimate && (
                        <>
                          <RowIconBtn
                            title="Email to customer"
                            testid={`customer-inv-email-${row.id}`}
                            icon={Mail}
                            tint="#f97316"
                            onClick={() => handOffToInvoices(row, 'email')}
                          />
                          <RowIconBtn
                            title="Edit"
                            testid={`customer-inv-edit-${row.id}`}
                            icon={Edit}
                            tint="#3b82f6"
                            onClick={() => handOffToInvoices(row, 'edit')}
                          />
                          <RowIconBtn
                            title={isPaid ? 'Fully paid' : 'Record payment'}
                            testid={`customer-inv-pay-${row.id}`}
                            icon={DollarSign}
                            tint="#22c55e"
                            disabled={isPaid}
                            onClick={() => handOffToInvoices(row, 'pay')}
                          />
                          <RowIconBtn
                            title="Send payment reminder"
                            testid={`customer-inv-reminder-${row.id}`}
                            icon={Bell}
                            tint="#0ea5e9"
                            disabled={isPaid}
                            onClick={() => handOffToInvoices(row, 'reminder')}
                          />
                          <RowIconBtn
                            title="Duplicate"
                            testid={`customer-inv-duplicate-${row.id}`}
                            icon={Copy}
                            tint="#8b5cf6"
                            onClick={() => handOffToInvoices(row, 'duplicate')}
                          />
                          <RowIconBtn
                            title="Delete"
                            testid={`customer-inv-delete-${row.id}`}
                            icon={Trash2}
                            tint="#ef4444"
                            onClick={() => handOffToInvoices(row, 'delete')}
                          />
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>

    <InvoicePreviewModal
      open={!!previewInvoice}
      previewInvoice={previewInvoice}
      companyInfo={companyInfo}
      companySignature={companySignature}
      printRef={printRef}
      onClose={() => setPreviewInvoice(null)}
      onDownload={(inv) => {
        generateInvoicePDF(inv, companyInfo);
        toast.success(`${inv._docType === 'estimate' ? 'Estimate' : 'Invoice'} PDF downloaded`);
      }}
    />
    </>
  );
}
